import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Menu,
  MenuItem,
} from "@mui/material";
import { ExtractedContract } from "../../../types/contract";
import DetailEditForm from "./DetailEditForm";
import ActionButtonGroup from "./ActionButtonGroup";
import { GradientBorderBox } from "./shared";

interface ContractDetailProps {
  selectedEditableContract: ExtractedContract | null;
  selectedContract: ExtractedContract | null;
  isEditing: boolean;
  isTrashView: boolean;
  loading: boolean;
  hasUnsavedChanges: boolean;
  aggregatedContracts: ExtractedContract[];
  changedContractIds: Set<number>;
  sourceMenuAnchorEl: HTMLElement | null;
  sourceFiles: string[];
  detailFields: (keyof ExtractedContract)[];
  labels: Record<string, string>;
  getEditableFieldValue: (contract: ExtractedContract, field: keyof ExtractedContract) => string;
  handleDetailChange: (field: keyof ExtractedContract, value: string) => void;
  handleOpenSourceMenu: (event: React.MouseEvent<HTMLElement>) => void;
  handleCloseSourceMenu: () => void;
  handleOpenFile: (file: string) => void;
  handlePrev: () => void;
  handleNext: () => void;
  handleRestore: (contract: ExtractedContract) => void;
  handleSaveSingleContractChanges: () => void;
  handleCancelEdit: () => void;
  handleStartEdit: () => void;
  handleDeleteDetails: (contract: ExtractedContract) => void;
  confirmAndNavigate: (action: () => void) => void;
  navigate: (path: string) => void;
  detailContentRef: React.Ref<HTMLDivElement>;
}

const ContractDetail: React.FC<ContractDetailProps> = ({
  selectedEditableContract,
  selectedContract,
  isEditing,
  isTrashView,
  loading,
  hasUnsavedChanges,
  aggregatedContracts,
  changedContractIds,
  sourceMenuAnchorEl,
  sourceFiles,
  detailFields,
  labels,
  getEditableFieldValue,
  handleDetailChange,
  handleOpenSourceMenu,
  handleCloseSourceMenu,
  handleOpenFile,
  handlePrev,
  handleNext,
  handleRestore,
  handleSaveSingleContractChanges,
  handleCancelEdit,
  handleStartEdit,
  handleDeleteDetails,
  confirmAndNavigate,
  navigate,
  detailContentRef,
}) => {
  return (
    <Box sx={{ position: 'relative' }}>
      {/* ローディングオーバーレイ */}
      {loading && selectedEditableContract && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            borderRadius: '8px',
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <GradientBorderBox innerRef={detailContentRef}>
        {loading && !selectedEditableContract ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              minHeight: "50vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : selectedEditableContract ? (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  flexGrow: 1,
                  mb: 0,
                  fontWeight: "bold",
                  ml: 2,
                  mt: -2,
                }}
              >
                {isTrashView ? "ごみ箱のデータ" : "詳細データ"}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  display: "inline-block",
                  color: "rgb(194, 140, 255)",
                  border: `2px solid rgb(194, 140, 255)`, // 内側の線
                  borderRadius: "50px", // 角丸
                  bgcolor: "white",
                  px: 2, // 横のパディング (theme spacing)
                  py: 0.2, // 縦のパディング
                  fontWeight: "bold",
                  fontSize: "1.1rem",

                  // ここがポイント
                  boxShadow: `
                    0 0 0 5px white,
                    4px 6px 10px rgba(176, 139, 248, 0.8)
                  `,
                    "&:hover": {
                    border: `2px solid rgb(194, 140, 255)`,
                    bgcolor: "rgba(194, 140, 255, 0.05)",
                    boxShadow: `
                      0 0 0 5px white,
                      6px 9px 10px rgba(176, 139, 248, 1) // 影が少し広がり、不透明度を上げる
                    `,
                  }}}
                onClick={handleOpenSourceMenu}
                disabled={sourceFiles.length === 0}
              >
                参照ファイル一覧
              </Button>
              <Menu
                anchorEl={sourceMenuAnchorEl}
                open={Boolean(sourceMenuAnchorEl)}
                onClose={handleCloseSourceMenu}
              >
                {sourceFiles.map((file) => (
                  <MenuItem
                    key={file}
                    onClick={() => {
                      handleOpenFile(file);
                      handleCloseSourceMenu();
                    }}
                  >
                    {file}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            <DetailEditForm
              detailFields={detailFields}
              labels={labels}
              selectedEditableContract={selectedEditableContract}
              isEditing={isEditing}
              isTrashView={isTrashView}
              getEditableFieldValue={getEditableFieldValue}
              handleDetailChange={handleDetailChange}
            />
            <ActionButtonGroup
              selectedContract={selectedContract}
              selectedEditableContract={selectedEditableContract}
              isEditing={isEditing}
              isTrashView={isTrashView}
              hasUnsavedChanges={hasUnsavedChanges}
              aggregatedContracts={aggregatedContracts}
              changedContractIds={changedContractIds}
              handlePrev={handlePrev}
              handleNext={handleNext}
              handleRestore={handleRestore}
              handleSaveSingleContractChanges={handleSaveSingleContractChanges}
              handleCancelEdit={handleCancelEdit}
              handleStartEdit={handleStartEdit}
              handleDeleteDetails={handleDeleteDetails}
              confirmAndNavigate={confirmAndNavigate}
              navigate={navigate}
            />
          </Box>
        ) : (
          <Typography>
            {isTrashView
              ? "ごみ箱は空です。"
              : "左のリストから項目を選択するか、フィルター条件を変更してください。"}
          </Typography>
        )}
      </GradientBorderBox>
    </Box>
  );
};

export default ContractDetail;
